import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
} from "react";
import handleError from "../constants/handleError";
import { useShowAlert } from "./AlertContext";
import AxiosInstance from "../utils/AxiosInstance";
import { useNetwork } from "./NetworkContext";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "./AuthProvider";

const ContextApi = createContext();

export const ContextApiProvider = ({ children }) => {
  const showAlert = useShowAlert();
  const { checkNetwork } = useNetwork();
  const [estimateSuggest, setEstimateSuggest] = useState([]);
  const [serviceNameSuggest, setServiceNameSuggest] = useState([]);
  const { isUser } = useAuth();
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [isCustomerData, setCustomerData] = useState(null);
  const currentTime = DateConverter.getCurrentTimeByTimeZone(
    isUser?.timezone,
  ).timeISO;

  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);

  const handleSuggestEstimate = async () => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/service/suggestion/title`,
      );

      setEstimateSuggest(response?.data?.results || []);
      return response?.data?.results || [];
      // setShowtitlePopup(true);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleSuggestService = async () => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/sale/suggestion/service-by-name`,
      );

      setServiceNameSuggest(response.data.results || []);
      return response.data.results || [];
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleCustomerSearch = (term) => {
    setCustomerSearchTerm(term);
    if (term.trim() === "") {
      setCustomerSuggestions([]);
      setCustomerData(null);
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        setIsCustomerLoading(true);
        if (!checkNetwork()) return;
        controllerRef.current = new AbortController();
        const response = await AxiosInstance.get(
          `module/customer/search?searchtxt=${encodeURIComponent(term.trim())}`,
          { signal: controllerRef.current.signal },
        );
        setCustomerSuggestions(response.data.results || []);
        const data = { phone: "", name: "" };
        if (/^\d+$/.test(term.trim())) {
          data.phone = term.trim();
        } else {
          data.name = term.trim();
        }
        setCustomerData(data);
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          handleError(error, showAlert);
        }
      } finally {
        setIsCustomerLoading(false);
      }
    }, 200);
  };

  let activeEscapeHandlers = [];

  const useEscapeKey = (callback) => {
    useEffect(() => {
      // Register this popup’s handler
      const handler = (e) => {
        if (e.key === "Escape") {
          // Only trigger the latest popup
          const isTopMost =
            activeEscapeHandlers[activeEscapeHandlers.length - 1] === handler;
          if (isTopMost) callback?.();
        }
      };

      activeEscapeHandlers.push(handler);
      window.addEventListener("keydown", handler);

      return () => {
        // Cleanup on close/unmount
        activeEscapeHandlers = activeEscapeHandlers.filter(
          (h) => h !== handler,
        );
        window.removeEventListener("keydown", handler);
      };
    }, [callback]);
  };

  const getColor = (date) => {
    const expire = new Date(
      DateConverter.convertToTimeZone(date, isUser?.timezone),
    );
    const now = new Date(currentTime);
    if (now > expire) return "bg-label-danger";
    else return "";
  };

  return (
    <ContextApi.Provider
      value={{
        handleSuggestEstimate,
        estimateSuggest,
        setEstimateSuggest,
        handleSuggestService,
        serviceNameSuggest,
        setServiceNameSuggest,
        customerSearchTerm,
        customerSuggestions,
        isCustomerLoading,
        isCustomerData,
        handleCustomerSearch,
        setCustomerData,
        setCustomerSuggestions,
        setCustomerSearchTerm,
        useEscapeKey,
        getColor,
      }}
    >
      {children}
    </ContextApi.Provider>
  );
};

export const useContextApi = () => useContext(ContextApi);
